import React, { useState, useRef, useCallback, useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
  TooltipItem
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { 
  ZoomIn,
  Download,
  Share2,
  Eye,
  EyeOff,
  TrendingUp,
  BarChart3
} from 'lucide-react';
import html2canvas from 'html2canvas';
import { SimulatorCartItem, TimePeriod, PERIOD_LABELS } from '../../types';
import { RevenueEvolution } from '../../../../utils/simulatorCalculations';
import { getEffectiveActCotationValue } from '../../../../utils/pricingUtils';
import { useToast } from '../../../../hooks/useToast';
import { trackSimulatorChartExported } from '../../../../utils/analyticsEvents';
import ChartModal from './ChartModal';
import ChartShareModal from './ChartShareModal';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface RevenueChartProps {
  cartItems: SimulatorCartItem[];
  itemEvolutions: Map<string, RevenueEvolution>;
  selectedPeriod: TimePeriod;
  isDrom: boolean;
  darkMode: boolean;
}

const RevenueChart: React.FC<RevenueChartProps> = ({
  cartItems,
  itemEvolutions,
  selectedPeriod,
  isDrom,
  darkMode
}) => {
  const chartRef = useRef<ChartJS<'line'>>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { success, error } = useToast();
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [visibleSeries, setVisibleSeries] = useState<Set<string>>(new Set(['total']));
  const [isExporting, setIsExporting] = useState(false);
  const [chartImageData, setChartImageData] = useState<string | null>(null);

  const chartData = useMemo(() => {
    if (cartItems.length === 0) return null;

    const now = new Date();
    const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

    const allPeriods: { label: string; key: string; date: string }[] = [
      { label: 'Original', key: 'original', date: '2024-01-01' },
      { label: 'Actuel', key: 'current', date: todayStr },
      { label: 'Janvier 2026', key: 'janv2026', date: '2026-01-01' },
      { label: 'Mai 2026', key: 'may2026', date: '2026-05-28' },
      { label: 'Sept. 2026', key: 'sept2026', date: '2026-09-01' },
      { label: 'Juillet 2027', key: 'july2027', date: '2027-07-01' },
    ];

    const periods = allPeriods.filter(p =>
      p.key === 'original' || p.key === 'current' || p.date > todayStr
    );

    const getEvolutionValue = (evolution: RevenueEvolution, key: string): number => {
      switch (key) {
        case 'original': return evolution.original.value;
        case 'current': return evolution.current.value;
        case 'janv2026': return evolution.janv2026?.value || evolution.current.value;
        case 'may2026': return evolution.may2026?.value || evolution.janv2026?.value || evolution.current.value;
        case 'sept2026': return evolution.september2026?.value || evolution.may2026?.value || evolution.janv2026?.value || evolution.current.value;
        case 'july2027': return evolution.july2027?.value || evolution.september2026?.value || evolution.may2026?.value || evolution.janv2026?.value || evolution.current.value;
        default: return 0;
      }
    };

    const totalRevenues = periods.map(p => {
      let total = 0;
      cartItems.forEach(item => {
        const evolution = itemEvolutions.get(item.id);
        if (!evolution) return;
        total += getEvolutionValue(evolution, p.key);
      });
      return Math.round(total * 100) / 100;
    });

    const datasets = [];

    // Serie principale: Emerald
    datasets.push({
      label: `Total (${PERIOD_LABELS[selectedPeriod].toLowerCase()})`,
      data: totalRevenues,
      borderColor: '#2BD199',
      backgroundColor: 'rgba(43, 209, 153, 0.1)',
      borderWidth: 4,
      pointRadius: 6,
      pointHoverRadius: 8,
      tension: 0.3,
      fill: true,
      hidden: !visibleSeries.has('total')
    });

    // Couleurs individuelles: Petrol, Sky, Honey, Petal, Glass
    const colors = ['#012A36', '#90B5FF', '#F2DA9A', '#FA9090', '#90EDD8'];

    cartItems.forEach((item, index) => {
      const evolution = itemEvolutions.get(item.id);
      if (!evolution) return;

      const actRevenues = periods.map(p => getEvolutionValue(evolution, p.key));
      const color = colors[index % colors.length];

      datasets.push({
        label: `${item.act.lettre_cle} ${getEffectiveActCotationValue(item.act)}`,
        data: actRevenues,
        borderColor: color,
        backgroundColor: `${color}20`,
        borderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
        tension: 0.2,
        hidden: !visibleSeries.has(item.id)
      });
    });

    return {
      labels: periods.map(p => p.label),
      datasets
    };
  }, [cartItems, itemEvolutions, selectedPeriod, visibleSeries]);

  const chartOptions: ChartOptions<'line'> = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      title: {
        display: true,
        text: `Évolution des Revenus - ${PERIOD_LABELS[selectedPeriod]}`,
        font: { size: 16, weight: 'bold' },
        color: darkMode ? '#FEFDFD' : '#012A36'
      },
      legend: {
        display: true,
        position: 'bottom' as const,
        labels: {
          color: darkMode ? 'rgba(254,253,253,0.65)' : 'rgba(1,42,54,0.65)',
          usePointStyle: true,
          padding: 20
        }
      },
      tooltip: {
        backgroundColor: darkMode ? '#012A36' : '#FEFDFD',
        titleColor: darkMode ? '#FEFDFD' : '#012A36',
        bodyColor: darkMode ? 'rgba(254,253,253,0.65)' : 'rgba(1,42,54,0.65)',
        borderColor: darkMode ? 'rgba(254,253,253,0.12)' : 'rgba(1,42,54,0.12)',
        borderWidth: 1,
        callbacks: {
          label: function(context: TooltipItem<'line'>) {
            const value = context.parsed.y;
            const label = context.dataset.label || '';
            const periodIndex = context.dataIndex;
            const currentPeriod = chartData?.labels?.[periodIndex] || '';

            if (currentPeriod !== 'Actuel' && chartData) {
              const currentValue = chartData.datasets[context.datasetIndex].data[1] as number;
              const difference = value - currentValue;
              const percentage = currentValue > 0 ? (difference / currentValue) * 100 : 0;

              return [
               `${label} : ${value.toFixed(2)}€`,
                `Écart vs Actuel: ${difference > 0 ? '+' : ''}${difference.toFixed(2)}€ (${percentage > 0 ? '+' : ''}${percentage.toFixed(1)}%)`
              ];
            }

           return `${label} : ${value.toFixed(2)}€`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: { color: darkMode ? 'rgba(254,253,253,0.08)' : 'rgba(1,42,54,0.08)' },
        ticks: { color: darkMode ? 'rgba(254,253,253,0.65)' : 'rgba(1,42,54,0.65)' }
      },
      y: {
        beginAtZero: true,
        grid: { color: darkMode ? 'rgba(254,253,253,0.08)' : 'rgba(1,42,54,0.08)' },
        ticks: {
          color: darkMode ? 'rgba(254,253,253,0.65)' : 'rgba(1,42,54,0.65)',
          callback: function(value) { return `${value}€`; }
        }
      }
    }
  }), [darkMode, selectedPeriod, chartData]);

  const toggleSeries = useCallback((seriesId: string) => {
    setVisibleSeries(prev => {
      const newSet = new Set(prev);
      if (newSet.has(seriesId)) { newSet.delete(seriesId); } else { newSet.add(seriesId); }
      return newSet;
    });
  }, []);

  const exportChart = useCallback(async () => {
    if (!containerRef.current) return;
    setIsExporting(true);
    try {
      const canvas = await html2canvas(containerRef.current, {
        backgroundColor: darkMode ? '#012A36' : '#FEFDFD',
        scale: 2, logging: false, useCORS: true
      });
      const imageData = canvas.toDataURL('image/png');
      setChartImageData(imageData);
      const link = document.createElement('a');
      link.download = `evolution-remuneration-ngap-${new Date().toISOString().split('T')[0]}.png`;
      link.href = imageData;
      link.click();
      trackSimulatorChartExported();
      success('Téléchargement réussi', 'Le graphique a été téléchargé en PNG');
      return imageData;
    } catch (err) {
      console.error('Export error:', err);
      error('Erreur d\'export', 'Impossible de télécharger le graphique');
      return null;
    } finally {
      setIsExporting(false);
    }
  }, [darkMode, success, error]);

  const generateShareableUrl = useCallback(() => {
    const params = new URLSearchParams();
    params.set('period', selectedPeriod);
    params.set('drom', isDrom.toString());
    const simplifiedCart = cartItems.map(item => ({ id: item.act.ID, qty: item.quantity, home: item.homeVisitPercentage }));
    params.set('cart', btoa(JSON.stringify(simplifiedCart)));
    return `${window.location.origin}/simulateur?${params.toString()}`;
  }, [cartItems, selectedPeriod, isDrom]);

  if (!chartData || cartItems.length === 0) {
    return (
      <div className={`rounded-lg border p-8 text-center ${
        darkMode ? 'bg-appines-petrol border-appines-white/12' : 'bg-appines-white border-[rgba(1,42,54,0.12)]'
      }`}>
        <BarChart3 className={`h-16 w-16 mx-auto mb-4 ${darkMode ? 'text-appines-white/25' : 'text-appines-petrol/25'}`} />
        <h3 className={`text-lg font-semibold mb-2 ${darkMode ? 'text-appines-white' : 'text-appines-petrol'}`}>
          Graphique d'évolution
        </h3>
        <p className={darkMode ? 'text-appines-white/65' : 'text-appines-petrol/65'}>
          Ajoutez des actes pour visualiser l'évolution de vos revenus
        </p>
      </div>
    );
  }

  const toggleBtnClass = (isActive: boolean, variant: 'primary' | 'secondary') =>
    `flex items-center px-3 py-1 rounded-full text-xs font-medium transition-colors ${
      isActive
        ? variant === 'primary'
          ? 'bg-appines-emerald text-appines-petrol'
          : 'bg-appines-petrol text-appines-white'
        : darkMode
          ? 'bg-appines-white/10 text-appines-white/65 hover:bg-appines-white/15'
          : 'bg-[rgba(1,42,54,0.06)] text-appines-petrol/65 hover:bg-[rgba(1,42,54,0.10)]'
    }`;

  const iconBtnClass = `p-2 rounded-lg transition-colors ${
    darkMode
      ? 'text-appines-white/65 hover:text-appines-white hover:bg-appines-white/10'
      : 'text-appines-petrol/65 hover:text-appines-petrol hover:bg-[rgba(1,42,54,0.06)]'
  }`;

  return (
    <>
      <div 
        ref={containerRef}
        className={`rounded-lg border p-6 ${
          darkMode ? 'bg-appines-petrol border-appines-white/12' : 'bg-appines-white border-[rgba(1,42,54,0.12)]'
        }`}
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <TrendingUp className="h-6 w-6 mr-3 text-appines-emerald" />
            <h3 className={`text-lg font-semibold ${darkMode ? 'text-appines-white' : 'text-appines-petrol'}`}>
              Évolution des Revenus
            </h3>
          </div>
          
          <div className="flex items-center space-x-2">
            <div className="hidden md:flex items-center space-x-2 mr-4">
              <button onClick={() => toggleSeries('total')} className={toggleBtnClass(visibleSeries.has('total'), 'primary')}>
                {visibleSeries.has('total') ? <Eye className="h-3 w-3 mr-1" /> : <EyeOff className="h-3 w-3 mr-1" />}
                Total
              </button>
              
              <button
                onClick={() => {
                  const allActIds = cartItems.map(item => item.id);
                  const allVisible = allActIds.every(id => visibleSeries.has(id));
                  setVisibleSeries(prev => {
                    const newSet = new Set(prev);
                    if (allVisible) { allActIds.forEach(id => newSet.delete(id)); } else { allActIds.forEach(id => newSet.add(id)); }
                    return newSet;
                  });
                }}
                className={toggleBtnClass(cartItems.some(item => visibleSeries.has(item.id)), 'secondary')}
              >
                {cartItems.some(item => visibleSeries.has(item.id)) ? <Eye className="h-3 w-3 mr-1" /> : <EyeOff className="h-3 w-3 mr-1" />}
                Actes
              </button>
            </div>

            <button onClick={() => setIsModalOpen(true)} className={iconBtnClass} title="Agrandir le graphique">
              <ZoomIn className="h-5 w-5" />
            </button>
            
            <button onClick={exportChart} disabled={isExporting} className={`${iconBtnClass} ${isExporting ? 'opacity-50 cursor-not-allowed' : ''}`} title="Télécharger en PNG">
              <Download className={`h-5 w-5 ${isExporting ? 'animate-pulse' : ''}`} />
            </button>
            
            <button onClick={() => setIsShareModalOpen(true)} className={iconBtnClass} title="Partager le graphique">
              <Share2 className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="relative h-80 md:h-96">
          <Line ref={chartRef} data={chartData} options={chartOptions} />
        </div>

        {/* Mobile legend */}
        <div className="md:hidden mt-4 space-y-2">
          <div className="flex flex-wrap gap-2">
            <button onClick={() => toggleSeries('total')} className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              visibleSeries.has('total') ? 'bg-appines-emerald text-appines-petrol' : darkMode ? 'bg-appines-white/10 text-appines-white/65' : 'bg-[rgba(1,42,54,0.06)] text-appines-petrol/65'
            }`}>
              {visibleSeries.has('total') ? <Eye className="h-4 w-4 mr-1" /> : <EyeOff className="h-4 w-4 mr-1" />}
              Total
            </button>
            
            {cartItems.map((item) => (
              <button
                key={item.id}
                onClick={() => toggleSeries(item.id)}
                className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  visibleSeries.has(item.id) ? 'bg-appines-petrol text-appines-white' : darkMode ? 'bg-appines-white/10 text-appines-white/65' : 'bg-[rgba(1,42,54,0.06)] text-appines-petrol/65'
                }`}
              >
                {visibleSeries.has(item.id) ? <Eye className="h-4 w-4 mr-1" /> : <EyeOff className="h-4 w-4 mr-1" />}
                {item.act.lettre_cle} {getEffectiveActCotationValue(item.act)}
              </button>
            ))}
          </div>
        </div>

        {/* Summary stats */}
        <div className={`mt-6 p-4 rounded-lg ${darkMode ? 'bg-appines-white/5' : 'bg-[rgba(1,42,54,0.04)]'}`}>
          <div className={`grid grid-cols-2 md:grid-cols-${Math.min(chartData.labels.length, 5)} gap-4 text-center`}>
            {chartData.labels.map((label, index) => {
              if (index === 0) return null;
              const value = chartData.datasets[0].data[index] as number;
              const currentValue = chartData.datasets[0].data[1] as number;
              const isCurrentPeriod = index === 1;
              return (
                <div key={label}>
                  <p className={`text-sm ${darkMode ? 'text-appines-white/45' : 'text-appines-petrol/65'}`}>
                    {isCurrentPeriod ? 'Revenus actuels' : label}
                  </p>
                  <p className={`text-lg font-bold ${
                    isCurrentPeriod
                      ? (darkMode ? 'text-appines-white' : 'text-appines-black')
                      : value > currentValue
                        ? 'text-appines-emerald'
                        : value < currentValue
                          ? 'text-[#c04040]'
                          : (darkMode ? 'text-appines-white' : 'text-appines-black')
                  }`}>
                    {value}€
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <ChartModal
        isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}
        chartData={chartData} chartOptions={chartOptions}
        onExport={exportChart} onShare={() => setIsShareModalOpen(true)}
        darkMode={darkMode} isExporting={isExporting}
      />

      <ChartShareModal
        isOpen={isShareModalOpen} onClose={() => setIsShareModalOpen(false)}
        shareableUrl={generateShareableUrl()} onExport={exportChart}
        darkMode={darkMode} chartTitle={`Évolution des Revenus NGAP - ${PERIOD_LABELS[selectedPeriod]}`}
        isExporting={isExporting} chartImageData={chartImageData}
      />
    </>
  );
};

export default RevenueChart;
